import React from 'react';
import PlotHoleCard from './PlotHoleCard';

/**
 * PlotHolesSection
 * 
 * Renders the plot holes section with severity summary bar
 * and a list of expandable PlotHoleCard components.
 * 
 * Props:
 * - plotHoles: object - { plotHoles: [], totalIssues, criticalCount, highSeverityCount, mediumSeverityCount, lowSeverityCount }
 * - expandedHoles: object - map of index -> boolean for expanded state
 * - onToggleHole: function(index) - callback to toggle a hole's expanded state
 * - title: optional section title
 * - renderHoleActions: optional callback to render custom hole actions
 * - levelLabel: optional label rendered on each hole card
 */
const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

export default function PlotHolesSection({
  plotHoles,
  expandedHoles,
  onToggleHole,
  title = 'Plot Holes',
  renderHoleActions = null,
  levelLabel = 'Project-level',
}) {
  if (!plotHoles?.plotHoles?.length) return null;

  const sorted = [...plotHoles.plotHoles].sort(
    (a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)
  );

  return (
    <div className="plot-section-compact plot-holes-section">
      <div className="section-label">
        {title}
        <span className="plot-holes-count">
          {plotHoles.totalIssues} issue{plotHoles.totalIssues !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="plot-holes-severity-bar">
        {plotHoles.criticalCount > 0 && (
          <span className="severity-tag critical">{plotHoles.criticalCount} critical</span>
        )}
        {plotHoles.highSeverityCount > 0 && (
          <span className="severity-tag high">{plotHoles.highSeverityCount} high</span>
        )}
        {plotHoles.mediumSeverityCount > 0 && (
          <span className="severity-tag medium">{plotHoles.mediumSeverityCount} medium</span>
        )}
        {plotHoles.lowSeverityCount > 0 && (
          <span className="severity-tag low">{plotHoles.lowSeverityCount} low</span>
        )}
      </div>
      <div className="plot-holes-list">
        {sorted.map((hole, index) => (
          <PlotHoleCard
            key={index}
            hole={hole}
            index={index}
            expanded={!!expandedHoles[index]}
            onToggle={onToggleHole}
            actions={renderHoleActions ? renderHoleActions(hole, index) : null}
            levelLabel={levelLabel}
          />
        ))}
      </div>
    </div>
  );
}
